var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Read the Redux store into the model-facing state of the ChatGPT embed
 * (CONTRACT-piab.md §1.1, §1.3) and build the vocabulary document (§1.2).
 * Pure functions of RootState; no dispatching here.
 */
import { t } from "i18next";
import { selectors } from "store/selectors";
import { NodeComponentType, ChecklistItemName, } from "store/types";
import { systemDataConstants } from "api/system-data";
import { ChoiceType, NumberType, } from "api/types";
import { objectShapes, tubeConnectionModels } from "3d/constants/common";
import { TubeConnectionId } from "store/scene/types.current";
import { getCupComponent } from "store/system-data/helpers";
import { getGoverningPump, getSuctionCups, getVacuumPumps, } from "ui/components/panels/graphs/common/helper-functions";
export var embedApiVersion = "piab-embed/v1";
var CURVE_POINTS = 200;
function readCurves(state, gripByNode, pumpByNode, required) {
    var e_1, _a, e_2, _b;
    var _c, _d, _e, _f;
    var g = selectors.selectGraphData(state);
    var sp = g.systemProperty;
    var n = sp.timeData.length;
    if (n < 2)
        return null;
    var inRange = function (i) { return Number.isInteger(i) && i >= 0 && i < n; };
    var keep = new Set();
    var step = (n - 1) / (CURVE_POINTS - 1);
    for (var k = 0; k < CURVE_POINTS; k++)
        keep.add(Math.min(n - 1, Math.round(k * step)));
    var markerIdx = [sp.psIndex, sp.esIndex, sp.placeStartIndex, sp.cupReleaseIndex];
    try {
        for (var markerIdx_1 = __values(markerIdx), markerIdx_1_1 = markerIdx_1.next(); !markerIdx_1_1.done; markerIdx_1_1 = markerIdx_1.next()) {
            var m = markerIdx_1_1.value;
            if (inRange(m)) {
                keep.add(m);
                if (m > 0)
                    keep.add(m - 1); // keep the step edges (blow-off is a jump) exact
                if (m + 1 < n)
                    keep.add(m + 1);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (markerIdx_1_1 && !markerIdx_1_1.done && (_a = markerIdx_1.return)) _a.call(markerIdx_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var markerSet = new Set(markerIdx.filter(inRange));
    // The API samples densely at the start (µs steps): collapse indices that round to the same time so `t` stays
    // strictly increasing; a marker index wins over a plain neighbour.
    var idx = [];
    try {
        for (var _g = __values(__spreadArray([], __read(keep), false).sort(function (a, b) { return a - b; })), _h = _g.next(); !_h.done; _h = _g.next()) {
            var i = _h.value;
            var prev = idx[idx.length - 1];
            if (prev !== undefined && round(sp.timeData[i], 3) === round(sp.timeData[prev], 3)) {
                if (markerSet.has(i) && !markerSet.has(prev))
                    idx[idx.length - 1] = i;
                continue;
            }
            idx.push(i);
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_h && !_h.done && (_b = _g.return)) _b.call(_g);
        }
        finally { if (e_2) throw e_2.error; }
    }
    var pick = function (arr, scale, d) {
        if (scale === void 0) { scale = 1; }
        if (d === void 0) { d = 3; }
        return idx.map(function (i) { var _a; return round(((_a = arr === null || arr === void 0 ? void 0 : arr[i]) !== null && _a !== void 0 ? _a : 0) * scale, d); });
    };
    var at = function (i) { return (inRange(i) ? round(sp.timeData[i], 3) : null); };
    var ps = (_c = at(sp.psIndex)) !== null && _c !== void 0 ? _c : 0;
    var cups = g.suctionCups.map(function (_a) {
        var _b, _c, _d;
        var id = _a.id, d = _a.performanceData;
        var grip = gripByNode.get(id);
        return { grip: (_b = grip === null || grip === void 0 ? void 0 : grip.index) !== null && _b !== void 0 ? _b : 0, label: (_d = (_c = grip === null || grip === void 0 ? void 0 : grip.cup) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : id, vacuum: pick(d.vacuumLevels, 1, 2) };
    });
    var pumps = g.vacuumPumps.map(function (_a) {
        var _b, _c, _d, _e;
        var id = _a.id, d = _a.performanceData;
        var pump = pumpByNode.get(id);
        var grip = gripByNode.get(id);
        return {
            pump: pump ? pump.index : grip ? "grip ".concat(grip.index) : id,
            label: (_e = (_c = (_b = pump === null || pump === void 0 ? void 0 : pump.model) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : (_d = grip === null || grip === void 0 ? void 0 : grip.pump) === null || _d === void 0 ? void 0 : _d.label) !== null && _e !== void 0 ? _e : id,
            vacuum: pick(d.vacuumLevels, 1, 2),
        };
    });
    return {
        t: pick(sp.timeData, 1, 3),
        markers: {
            ps: ps,
            es: sp.showEs && sp.esIndex > sp.psIndex ? at(sp.esIndex) : null,
            placeStart: (_d = at(sp.placeStartIndex)) !== null && _d !== void 0 ? _d : ps,
            release: (_f = (_e = at(sp.cupReleaseIndex)) !== null && _e !== void 0 ? _e : at(sp.placeStartIndex)) !== null && _f !== void 0 ? _f : ps,
        },
        air: pick(sp.airConsumptionVsTime, 60, 2), // Nl/s -> Nl/min, as the app's graph does
        motion: pick(sp.robotMotionVsTime, 1, 1),
        liftV: pick(sp.liftingForceVerticalVsTime, 1, 1),
        liftH: pick(sp.liftingForceHorizontalVsTime, 1, 1),
        requiredLiftV: round(required.requiredLiftingForceVertical, 1),
        requiredLiftH: round(required.requiredLiftingForceHorizontal, 1),
        cups: cups,
        pumps: pumps,
    };
}
var round = function (n, d) {
    if (d === void 0) { d = 2; }
    var f = Math.pow(10, d);
    return Math.round(n * f) / f;
};
var ref = function (c) {
    return c
        ? __assign({ value: c.value, label: c.label }, (c.cupFamily ? { family: c.cupFamily } : {})) : null;
};
export function isCentral(state) {
    return selectors.selectIsCentralizedSystem(state) !== false;
}
/** Nodes in insertion order (Object key order is insertion order in immer/RTK state). */
export function nodesOfType(state, type) {
    return Object.values(state.scene.present.nodes).filter(function (n) { return n.type === type; });
}
export function gripNodes(state) {
    return nodesOfType(state, NodeComponentType.GRIP_POSITION);
}
export function pumpNodes(state) {
    return nodesOfType(state, NodeComponentType.CENTRALIZED_VACUUM_PUMP);
}
export function tubeConnectionNodes(state) {
    return nodesOfType(state, NodeComponentType.TUBE_CONNECTION);
}
function nodeConnected(state, node, needed) {
    var attachments = state.scene.present.attachments;
    var ids = node.attachmentIds;
    if (ids.length < needed)
        return false;
    return ids.every(function (id) { var _a; return !!((_a = attachments[id]) === null || _a === void 0 ? void 0 : _a.edgeId); });
}
export function objectShapeLabel(id) {
    var shape = objectShapes[id];
    if (!shape)
        return id;
    var label = t(shape.translationKey);
    return label && label !== shape.translationKey ? label : id;
}
export function tubeConnectionLabel(type) {
    var _a;
    var _b;
    var keyMap = (_a = {},
        _a[TubeConnectionId.THREE_WAY_T] = "three_way_t",
        _a[TubeConnectionId.THREE_WAY_Y] = "three_way_y",
        _a[TubeConnectionId.TWO_WAY_90] = "two_way_90",
        _a[TubeConnectionId.FOUR_WAY_X] = "four_way_x",
        _a[TubeConnectionId.FOUR_WAY_TT] = "four_way_tt",
        _a[TubeConnectionId.FOUR_WAY_TT_ALTERNATE] = "four_way_tt_alternate",
        _a[TubeConnectionId.FOUR_WAY_TT_ALTERNATE_MIRROR] = "four_way_tt_alternate_mirror",
        _a[TubeConnectionId.TOOL_CHANGER] = "tool_changer",
        _a);
    var key = "sidebar.edit_tube_connection.connection_point_types.".concat((_b = keyMap[type]) !== null && _b !== void 0 ? _b : type);
    var label = t(key);
    return label && label !== key ? label : type;
}
export function tubeConnectionPorts(type) {
    var _a;
    var model = tubeConnectionModels[type];
    return (_a = model === null || model === void 0 ? void 0 : model.placeholderNames.length) !== null && _a !== void 0 ? _a : 0;
}
function parameterEntry(p, systemData) {
    var out = { label: p.label, value: p.value };
    if (p.type === ChoiceType.DROPDOWN || p.type === ChoiceType.IMAGE_SELECT) {
        var options = p.options.map(function (o) { return ({ value: o.value, label: o.label }); });
        out.options = options;
        var current = options.find(function (o) { return o.value === p.value; });
        if (current)
            out.valueLabel = current.label;
    }
    else if (p.type === ChoiceType.SLIDER) {
        out.min = p.sliderMin;
        out.max = p.sliderMax;
    }
    else if (p.type === NumberType.INT || p.type === NumberType.FLOAT) {
        if (p.min !== undefined)
            out.min = p.min;
        if (p.max !== undefined)
            out.max = p.max;
    }
    if (p.unit)
        out.unit = String(p.unit);
    var group = systemData.parameterGroups[p.groupId];
    if (group === null || group === void 0 ? void 0 : group.title)
        out.group = group.title;
    return out;
}
/** User-facing Tacton parameters (define-parameters step): not read-only, in a visible group. */
export function userParameters(systemData) {
    if (!systemData)
        return [];
    return Object.values(systemData.parameters).filter(function (p) {
        return !p.readonly &&
            p.groupId !== systemDataConstants.parameterNames.applicationField;
    });
}
/**
 * The app's system messages (hooks/messages.ts) as pure functions of the
 * state: errors, warnings and info the topbar would show, translated.
 */
export function systemMessages(state) {
    var _a, _b, _c;
    var errors = [];
    var warnings = [];
    var info = [];
    var perf = state.systemPerformance;
    var sanity = perf.sanityCheckData;
    var post = perf.isPerformanceDataValid;
    var mpa = function (v) { return "".concat(round(v, 2), " MPa"); };
    var fp = sanity
        ? {
            feedPressureActual: mpa(sanity.feedPressureActual),
            feedPressureRangeMin: mpa(sanity.feedPressureRangeMin),
            feedPressureRangeMax: mpa(sanity.feedPressureRangeMax),
            feedPressureDrop: mpa(sanity.feedPressureDrop),
        }
        : {};
    var phase = post ? "postcalculate" : "precalculate";
    if (sanity) {
        if (!sanity.cycleTimeReachable)
            errors.push(t("system_errors.cycle_time_not_reachable"));
        if (perf.hasSettledUserCalculation && !sanity.partSecuredReachable)
            errors.push(t("system_errors.part_secured_level_not_reachable"));
        if (sanity.energySaveAvailable && !sanity.energySaveReachable)
            errors.push(t("system_errors.energy_saving_level_not_reachable"));
        if (sanity.feedPressureMessage === "feedpressure_too_low")
            errors.push(t("system_errors.feed_pressure_too_low", fp));
        if (sanity.feedPressureMessage === "feedpressure_too_high")
            errors.push(t("system_errors.feed_pressure_too_high", fp));
        if (sanity.freeRunningVacuumLevelMessage === "tubing_much_too_small")
            errors.push(t("system_errors.tubing_much_too_small"));
        if (sanity.freeRunningVacuumLevelMessage === "tubing_too_large")
            warnings.push(t("system_info.tubing_too_large.".concat(phase)));
        if (sanity.freeRunningVacuumLevelMessage === "tubing_too_small")
            warnings.push(t("system_info.tubing_too_small.".concat(phase)));
        if (sanity.feedPressureMessage === "feedpressure_below_normal_operating_range")
            warnings.push(t("system_warnings.feedpressure_below_normal_operating_range.".concat(phase), fp));
        if (sanity.feedPressureMessage === "feedpressure_above_normal_operating_range")
            warnings.push(t("system_warnings.feedpressure_above_normal_operating_range.".concat(phase), fp));
        if (sanity.feedPressureDropQuality === "tubing_too_small")
            warnings.push(t("system_warnings.feed_pressure_drop_tubing_too_small.".concat(phase), fp));
        if (sanity.feedPressureDropQuality === "tubing_too_large")
            warnings.push(t("system_warnings.feed_pressure_drop_tubing_too_large.".concat(phase), fp));
        if (sanity.freeRunningVacuumLevelMessage === "tubing_correctly_sized")
            info.push(t("system_info.tubing_correctly_sized.".concat(phase)));
        if (sanity.feedPressureMessage ===
            "feedpressure_within_normal_operating_range")
            info.push(t("system_info.feed_pressure_within_normal_operating_range.".concat(phase), fp));
        if (sanity.feedPressureDropQuality === "tubing_correctly_sized")
            info.push(t("system_info.feed_pressure_drop_tubing_correctly_sized", fp));
    }
    var output = (_a = state.systemDataDefineParameters.dataState.data) === null || _a === void 0 ? void 0 : _a.output;
    if (output && gripNodes(state).length) {
        var active = post
            ? selectors.selectActivePerformanceData(state)
            : undefined;
        var actualH = post
            ? (_b = active === null || active === void 0 ? void 0 : active.totalHorizontalLiftForceAtPS) !== null && _b !== void 0 ? _b : 0
            : output.totalLiftingForceHorizontal;
        var actualV = post
            ? (_c = active === null || active === void 0 ? void 0 : active.totalVerticalLiftForceAtPS) !== null && _c !== void 0 ? _c : 0
            : output.totalLiftingForceVertical;
        if (Math.round(actualH) < Math.round(output.requiredLiftingForceHorizontal) ||
            Math.round(actualV) < Math.round(output.requiredLiftingForceVertical))
            warnings.push(t("system_warnings.required_lifting_force_not_achieved"));
    }
    return { errors: errors, warnings: warnings, info: info.map(function (x) { return x.replace(/\\n/g, " "); }) };
}
function structuralIssues(state) {
    var e_3, _a;
    var _b;
    var issues = [];
    var structural = selectors.selectCalculatePerformanceStructuralErrorMessage(state);
    if (structural)
        issues.push(t(structural));
    var perf = state.systemPerformance;
    if (perf.hasSettledUserCalculation && perf.sanityCheckData) {
        if (!perf.sanityCheckData.validationPassed)
            issues.push(t("notifications.sanity_check_failed"));
        else if (!perf.sanityCheckData.partSecuredReachable)
            issues.push(t("notifications.full_calculation_ps_not_reached"));
    }
    if (((_b = perf.error) === null || _b === void 0 ? void 0 : _b.visible) && perf.error.message)
        issues.push("Calculation error: ".concat(perf.error.message));
    try {
        for (var _c = __values(systemMessages(state).errors), _d = _c.next(); !_d.done; _d = _c.next()) {
            var e = _d.value;
            if (!issues.includes(e))
                issues.push(e);
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_3) throw e_3.error; }
    }
    return __spreadArray([], __read(new Set(issues)), false).map(function (x) { return x.replace(/\\n/g, " "); });
}
export function readPerformance(state, grips, pumps) {
    var _a, _b, _c, _d, _e;
    var perf = state.systemPerformance;
    if (!perf.isPerformanceDataValid || !perf.data)
        return null;
    var active = selectors.selectActivePerformanceData(state);
    var dashboard = selectors.selectDashboardData(state);
    var sanity = perf.sanityCheckData;
    var systemData = (_a = perf.systemDataSnapshot) !== null && _a !== void 0 ? _a : state.systemDataDefineParameters.dataState.data;
    if (!active || !systemData)
        return null;
    var messages = systemMessages(state);
    var warnings = __spreadArray(__spreadArray([], __read(messages.errors), false), __read(messages.warnings), false).map(function (x) {
        return x.replace(/\\n/g, " ");
    });
    var notes = messages.info;
    var energySaving = perf.showEnergySavePerformance && !!perf.data.energySavingOn;
    var cycle = null;
    var air = null;
    var energy = null;
    if (dashboard) {
        var s = dashboard.applicationSpeed;
        var before = s.vacuumOnBeforePick.value + s.blowOffOnBeforePick.value;
        var cycleTime = before +
            s.timeToPartSecured.value +
            s.transferToPlace.value +
            s.totalReleaseTime.value +
            s.returnToPick.value;
        var targetCycleTime = dashboard.targetSecsPerCycle.value;
        cycle = {
            timeToPartSecured: round(s.timeToPartSecured.value, 3),
            timeToEnergySave: active.systemTimeToEnergySave > 0
                ? round(active.systemTimeToEnergySave, 3)
                : null,
            transferToPlace: round(s.transferToPlace.value, 3),
            releaseTime: round(s.totalReleaseTime.value, 3),
            returnToPick: round(s.returnToPick.value, 3),
            cycleTime: round(cycleTime, 2),
            targetCycleTime: round(targetCycleTime, 2),
            targetPartsPerMinute: dashboard.targetPartsPerMinute.value,
            achievedPartsPerMinute: cycleTime > 0 ? round(60 / cycleTime, 1) : 0,
            meetsTarget: cycleTime <= targetCycleTime + 1e-6,
        };
        air = {
            perCycleNl: round(dashboard.airConsumption.perCycle.value, 2),
            perMinuteNlMin: round(dashboard.airConsumption.perMinute.value, 1),
        };
        energy = {
            perCycleWh: round(dashboard.energyUsage.total.value, 3),
            perDayKwh: round(dashboard.energyUsage.perDay.value, 3),
            compressorPowerW: round(dashboard.energyUsage.compressorPower.value, 1),
        };
    }
    var required = systemData.output;
    var vertical = (_b = active.totalVerticalLiftForceAtPS) !== null && _b !== void 0 ? _b : null;
    var horizontal = (_c = active.totalHorizontalLiftForceAtPS) !== null && _c !== void 0 ? _c : null;
    var lifting = {
        verticalAtPS: vertical === null ? null : round(vertical, 1),
        horizontalAtPS: horizontal === null ? null : round(horizontal, 1),
        requiredVertical: round(required.requiredLiftingForceVertical, 1),
        requiredHorizontal: round(required.requiredLiftingForceHorizontal, 1),
        ok: vertical === null || horizontal === null
            ? null
            : vertical >= required.requiredLiftingForceVertical &&
                horizontal >= required.requiredLiftingForceHorizontal,
    };
    var feedPressure = sanity
        ? {
            actual: round(sanity.feedPressureActual, 3),
            recommended: round(sanity.feedPressureRecommended, 3),
            drop: round(sanity.feedPressureDrop, 3),
            quality: sanity.feedPressureDropQuality,
            message: (_d = sanity.feedPressureMessage) !== null && _d !== void 0 ? _d : "",
        }
        : null;
    var gripByNode = new Map(grips.map(function (g) { return [g.id, g]; }));
    var pumpByNode = new Map(pumps.map(function (p) { return [p.id, p]; }));
    var governing = getGoverningPump(active.nodeData, (_e = sanity === null || sanity === void 0 ? void 0 : sanity.governingPumpId) !== null && _e !== void 0 ? _e : -1);
    var cups = getSuctionCups(active.nodeData).map(function (_a) {
        var _b, _c, _d;
        var id = _a.id, d = _a.performanceData;
        var grip = gripByNode.get(id);
        return {
            grip: (_b = grip === null || grip === void 0 ? void 0 : grip.index) !== null && _b !== void 0 ? _b : 0,
            label: (_d = (_c = grip === null || grip === void 0 ? void 0 : grip.cup) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : id,
            timeToPS: round(d.timeToPartSecured, 3),
            vacuumAtPS: round(d.vacuumLevelAtSystemPS, 1),
            finalVacuum: round(d.finalVacuumLevel, 1),
            liftVerticalAtPS: round(d.liftingForceVerticalAtPS, 1),
            liftHorizontalAtPS: round(d.liftingForceHorizontalAtPS, 1),
        };
    });
    var pumpRows = getVacuumPumps(active.nodeData).map(function (_a) {
        var _b, _c, _d, _e;
        var id = _a.id, d = _a.performanceData;
        var pump = pumpByNode.get(id);
        var grip = gripByNode.get(id);
        return {
            pump: pump ? pump.index : grip ? "grip ".concat(grip.index) : id,
            label: (_e = (_c = (_b = pump === null || pump === void 0 ? void 0 : pump.model) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : (_d = grip === null || grip === void 0 ? void 0 : grip.pump) === null || _d === void 0 ? void 0 : _d.label) !== null && _e !== void 0 ? _e : id,
            governing: governing.id === id,
            timeToPS: round(d.timeToPartSecured, 3),
            freeRunningVacuum: round(d.freeRunningVacuumLevel, 1),
            airConsumptionNlMin: round(d.airConsumptionTotal * 60, 1),
        };
    });
    return {
        status: "ok",
        energySaving: energySaving,
        cycle: cycle,
        air: air,
        energy: energy,
        lifting: lifting,
        feedPressure: feedPressure,
        cups: cups,
        pumps: pumpRows,
        warnings: warnings,
        notes: notes,
        curves: safeCurves(state, gripByNode, pumpByNode, required),
    };
}
function safeCurves() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    try {
        return readCurves.apply(void 0, __spreadArray([], __read(args), false));
    }
    catch (error) {
        console.debug("[embed] curves failed", error);
        return null;
    }
}
export function readState(state) {
    var e_4, _a;
    var _b, _c, _d, _e;
    var systemData = state.systemDataDefineParameters.dataState.data;
    var appParam = selectors.selectApplicationNames(state);
    var application = appParam
        ? {
            id: appParam.value,
            label: (_c = (_b = appParam.options.find(function (o) { return o.value === appParam.value; })) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : appParam.value,
        }
        : null;
    var central = isCentral(state);
    var scene = state.scene.present;
    var shape = scene.objectShape;
    var shapeData = objectShapes[shape];
    var object = {
        shape: shape,
        label: objectShapeLabel(shape),
        adjustable: !!(shapeData === null || shapeData === void 0 ? void 0 : shapeData.adjustableObject),
    };
    if (object.adjustable)
        object.size = {
            x: round(scene.objectSize.x, 3),
            y: round(scene.objectSize.y, 3),
            z: round(scene.objectSize.z, 3),
        };
    var parameters = {};
    if (systemData) {
        try {
            for (var _f = __values(userParameters(systemData)), _g = _f.next(); !_g.done; _g = _f.next()) {
                var p = _g.value;
                parameters[p.id] = parameterEntry(p, systemData);
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
            }
            finally { if (e_4) throw e_4.error; }
        }
    }
    var sd = state.systemDataDefineParameters;
    var grips = gripNodes(state).map(function (node, i) {
        var cup = getCupComponent(node.id, sd.cupSelectionMap, systemData);
        var mounting = selectors.selectSelectedCupMounting(node.id)(state);
        var technology = selectors.selectSelectedCupTechnology(node.id)(state);
        var pump = central
            ? undefined
            : selectors.selectSelectedDecentralPump(node.id)(state);
        return {
            index: i + 1,
            id: node.id,
            position: { x: round(node.position.x, 3), z: round(node.position.z, 3) },
            cup: ref(cup),
            mounting: ref(mounting),
            technology: ref(technology),
            pump: ref(pump),
            connected: nodeConnected(state, node, 1),
        };
    });
    var pumps = central
        ? pumpNodes(state).map(function (node, i) { return ({
            index: i + 1,
            id: node.id,
            model: ref(selectors.selectSelectedCentralPump(node.id)(state)),
            position: {
                x: round(node.position.x, 3),
                z: round(node.position.z, 3),
            },
            connected: nodeConnected(state, node, 2),
        }); })
        : [];
    var tubeConnections = tubeConnectionNodes(state).map(function (node, i) {
        var type = node.component.type;
        var free = node.attachmentIds.filter(function (id) { var _a; return !((_a = scene.attachments[id]) === null || _a === void 0 ? void 0 : _a.edgeId); }).length;
        return {
            index: i + 1,
            id: node.id,
            type: type,
            label: tubeConnectionLabel(type),
            ports: tubeConnectionPorts(type),
            free: free,
        };
    });
    var tubes = {
        count: Object.keys(scene.edges).length,
        allConnected: !!((_d = state.ui.checklist[ChecklistItemName.CONNECT_PARTS]) === null || _d === void 0 ? void 0 : _d.completed),
    };
    var issues = structuralIssues(state);
    var performance = readPerformance(state, grips, pumps);
    var result = {
        application: application,
        systemType: central ? "central" : "decentral",
        object: object,
        parameters: parameters,
        grips: grips,
        pumps: pumps,
        tubeConnections: tubeConnections,
        tubes: tubes,
        outputs: (_e = systemData === null || systemData === void 0 ? void 0 : systemData.output) !== null && _e !== void 0 ? _e : null,
        issues: issues,
        performance: performance,
        summary: { text: "" },
    };
    result.summary.text = summarize(result);
    return result;
}
function countLabels(items) {
    var e_5, _a;
    var _b, _c, _d;
    var counts = new Map();
    try {
        for (var items_1 = __values(items), items_1_1 = items_1.next(); !items_1_1.done; items_1_1 = items_1.next()) {
            var item = items_1_1.value;
            counts.set((_b = item === null || item === void 0 ? void 0 : item.label) !== null && _b !== void 0 ? _b : "(none)", ((_d = counts.get((_c = item === null || item === void 0 ? void 0 : item.label) !== null && _c !== void 0 ? _c : "(none)")) !== null && _d !== void 0 ? _d : 0) + 1);
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (items_1_1 && !items_1_1.done && (_a = items_1.return)) _a.call(items_1);
        }
        finally { if (e_5) throw e_5.error; }
    }
    return __spreadArray([], __read(counts.entries()), false).map(function (_a) {
        var _b = __read(_a, 2), label = _b[0], n = _b[1];
        return "".concat(label, " \u00D7").concat(n);
    }).join(", ");
}
export function summarize(s) {
    var _a, _b, _c, _d, _e, _f;
    var parts = [];
    var app = (_b = (_a = s.application) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : "Unknown application";
    var size = s.object.size ? " ".concat(s.object.size.x, "\u00D7").concat(s.object.size.z, " m") : "";
    parts.push("".concat(app, ", ").concat(s.systemType, " system on ").concat(s.object.label).concat(size, "."));
    if (s.grips.length === 0) {
        parts.push("No grip positions placed yet.");
    }
    else {
        parts.push("".concat(s.grips.length, " grip position").concat(s.grips.length > 1 ? "s" : "", ": cups ").concat(countLabels(s.grips.map(function (g) { return g.cup; })), "."));
        var mountings = s.grips.filter(function (g) { return g.mounting; });
        if (mountings.length)
            parts.push("Mountings: ".concat(countLabels(mountings.map(function (g) { return g.mounting; })), "."));
        var tech = s.grips.filter(function (g) { return g.technology; });
        if (tech.length)
            parts.push("Technologies: ".concat(countLabels(tech.map(function (g) { return g.technology; })), "."));
        if (s.systemType === "decentral")
            parts.push("Decentral pumps: ".concat(countLabels(s.grips.map(function (g) { return g.pump; })), "."));
    }
    if (s.systemType === "central") {
        parts.push(s.pumps.length
            ? "".concat(s.pumps.length, " central pump").concat(s.pumps.length > 1 ? "s" : "", ": ").concat(countLabels(s.pumps.map(function (p) { return p.model; })), ".")
            : "No central pump yet.");
    }
    if (s.tubeConnections.length)
        parts.push("".concat(s.tubeConnections.length, " tube connection").concat(s.tubeConnections.length > 1 ? "s" : "", " (").concat(s.tubeConnections.map(function (c) { return c.type; }).join(", "), ")."));
    parts.push(s.tubes.count
        ? "".concat(s.tubes.count, " tube").concat(s.tubes.count > 1 ? "s" : "").concat(s.tubes.allConnected
            ? ", all parts connected"
            : ", NOT all parts connected", ".")
        : "No tubes yet.");
    var fp = s.parameters[systemDataConstants.parameterNames.feedPressure];
    var ps = s.parameters[systemDataConstants.parameterNames.partSecuredLevel];
    var ppm = s.parameters[systemDataConstants.parameterNames.targetPartsPerMinute];
    var weight = s.parameters[systemDataConstants.parameterNames.objectWeight];
    var keyParams = [
        fp && "feed pressure ".concat(fp.value, " ").concat((_c = fp.unit) !== null && _c !== void 0 ? _c : "").trim(),
        ps && "PS level ".concat(ps.value, " ").concat((_d = ps.unit) !== null && _d !== void 0 ? _d : "").trim(),
        ppm && "target ".concat(ppm.value, " parts/min"),
        weight && "object ".concat(weight.value, " ").concat((_e = weight.unit) !== null && _e !== void 0 ? _e : "kg").trim(),
    ].filter(Boolean);
    if (keyParams.length)
        parts.push("Parameters: ".concat(keyParams.join(", "), "."));
    if (s.outputs && s.grips.length) {
        var o = s.outputs;
        var okV = Math.round(o.totalLiftingForceVertical) >=
            Math.round(o.requiredLiftingForceVertical);
        var okH = Math.round(o.totalLiftingForceHorizontal) >=
            Math.round(o.requiredLiftingForceHorizontal);
        parts.push("Lifting force (Tacton): ".concat(round(o.totalLiftingForceVertical, 0), " N vertical / ").concat(round(o.totalLiftingForceHorizontal, 0), " N horizontal available vs ").concat(round(o.requiredLiftingForceVertical, 0), " / ").concat(round(o.requiredLiftingForceHorizontal, 0), " N required").concat(okV && okH
            ? " (sufficient)"
            : " — INSUFFICIENT, add cups or pick bigger cups", "."));
    }
    if ((_f = s.performance) === null || _f === void 0 ? void 0 : _f.cycle) {
        var c = s.performance.cycle;
        parts.push("Simulated: cycle ".concat(c.cycleTime, " s vs target ").concat(c.targetCycleTime, " s (").concat(c.meetsTarget ? "meets" : "MISSES", " target), time to part secured ").concat(c.timeToPartSecured, " s."));
        if (s.performance.warnings.length)
            parts.push("Warnings: ".concat(s.performance.warnings.join(" ")));
    }
    else {
        parts.push("Not simulated yet.");
    }
    return parts.join(" ");
}
export function validComponents(systemData) {
    var valid = function (list) {
        return Object.values(list !== null && list !== void 0 ? list : {}).filter(function (c) { return c.valid && !c.value.endsWith("_X"); });
    };
    return {
        cups: valid(systemData === null || systemData === void 0 ? void 0 : systemData.suctionCupComponents),
        mountings: valid(systemData === null || systemData === void 0 ? void 0 : systemData.cupMountingComponents),
        technologies: valid(systemData === null || systemData === void 0 ? void 0 : systemData.cupTechnologyComponents),
        pumps: valid(systemData === null || systemData === void 0 ? void 0 : systemData.vacuumPumpComponents),
    };
}
export function buildOptions(state) {
    var e_6, _a;
    var _b;
    var systemData = state.systemDataDefineParameters.dataState.data;
    var appParam = selectors.selectApplicationNames(state);
    var applications = appParam
        ? appParam.options.map(function (o) { return ({ id: o.value, label: o.label }); })
        : [];
    var parameters = {};
    if (systemData) {
        try {
            for (var _c = __values(userParameters(systemData)), _d = _c.next(); !_d.done; _d = _c.next()) {
                var p = _d.value;
                var e = parameterEntry(p, systemData);
                parameters[p.id] = __assign(__assign(__assign(__assign(__assign({ label: e.label, type: e.options ? "choice" : "number" }, (e.unit ? { unit: e.unit } : {})), (e.min !== undefined ? { min: e.min } : {})), (e.max !== undefined ? { max: e.max } : {})), (e.options ? { options: e.options } : {})), (e.group ? { group: e.group } : {}));
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_6) throw e_6.error; }
        }
    }
    var activeApplication = selectors.selectActiveApplication(state);
    var shapes = selectors.selectObjectShapes(state);
    var objectShapesOut = {};
    objectShapesOut[activeApplication !== null && activeApplication !== void 0 ? activeApplication : "current"] = Object.keys(shapes).map(function (id) { return (__assign({ id: id, label: objectShapeLabel(id) }, (shapes[id].adjustableObject
        ? { adjustable: true, sizeRange: shapes[id].sizeRange }
        : {}))); });
    var c = validComponents(systemData);
    var components = {
        cups: c.cups.map(function (x) { return ({
            value: x.value,
            label: x.label,
            family: x.cupFamily,
            lipMm: x.width && x.height
                ? "".concat(Math.round(x.width * 1000), "x").concat(Math.round(x.height * 1000))
                : undefined,
            mounting: x.mountingCompatibility,
        }); }),
        mountings: c.mountings.map(function (x) { return ({
            value: x.value,
            label: x.label,
            type: x.model,
        }); }),
        technologies: c.technologies.map(function (x) { return ({
            value: x.value,
            label: x.label,
            type: x.type,
        }); }),
        pumps: c.pumps.map(function (x) { return ({
            value: x.value,
            label: x.label,
            model: x.model,
            energySaving: x.isEnergySavingAvailable,
            inline: x.isInlineDecentralPump,
        }); }),
    };
    var tubeConnectionTypes = Object.values(TubeConnectionId).map(function (id) { return ({
        id: id,
        label: tubeConnectionLabel(id),
        ports: tubeConnectionPorts(id),
    }); });
    return {
        api: "piab/v1",
        generatedFrom: "piab-client ".concat((_b = process.env.VERSION) !== null && _b !== void 0 ? _b : "", " live ").concat(new Date()
            .toISOString()
            .slice(0, 10)).trim(),
        applications: applications,
        systemTypes: [
            {
                id: "central",
                label: "Centralized",
                tacton: systemDataConstants.parameterValues.centralizedSystem,
            },
            {
                id: "decentral",
                label: "Decentralized",
                tacton: systemDataConstants.parameterValues.decentralizedSystem,
            },
        ],
        parameters: parameters,
        objectShapes: objectShapesOut,
        components: components,
        tubeConnectionTypes: tubeConnectionTypes,
    };
}
/** Every object shape id the app knows (all applications), for validation. */
export function allObjectShapeIds() {
    return Object.keys(objectShapes);
}
